import { Injectable } from '@angular/core';
import { Participant } from '../../shared/models/participant';
import { Observable } from 'rxjs';
import { BaseList, BaseWithIdList } from '../../shared/models/list';
import { Case } from '../../shared/models/case';
import { DRResponse } from '../../shared/models/response';
import { map } from 'rxjs/operators';
import { ParticipantType } from '../../shared/models/participant-type';
import { TranslateService } from '@ngx-translate/core';
import { TranslationEntry } from '../../shared/models/translatable';
import {
  BaseHttpService, Create, Delete,
  GetById, Patch,
  Query,
  UriBuilderParams
} from '@app/core/services/base/base-http.service';
import { HttpServiceBundle } from '@app/core/services/base/http-service-bundle.service';
import { DrQueryParams, OpData } from '@app/core/services/base/base-service-with-bundle.service';
import { BackendUriUnits } from '@app/core/services/base/backend-uri-units';
import { WorkflowParticipantUriBuilderParams } from '@app/core/services/workflow/workflow-participants.service';
import { environment } from '../../../environments/environment';

export class CaseParticipantUriBuilderParams extends UriBuilderParams<Participant> {
  private readonly _case: string;

  get case() {
    return this._case;
  }

  constructor(caseX: Case | string, participant?: Participant | string) {
    super(participant);
    this._case = this.getParamId(caseX);
  }

  public buildUri(): string {
    let uri = `/${BackendUriUnits.CASES}/${this._case}`;
    uri += this.simpleUriBuild(BackendUriUnits.PARTICIPANTS);
    return uri;
  }

  public buildTypeUri(): string {
    let uri = `/${BackendUriUnits.CASES}/${this._case}`;
    uri += this.simpleUriBuild(`${BackendUriUnits.PARTICIPANTS}_types`);
    return uri;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ParticipantsService
        extends BaseHttpService<Participant, CaseParticipantUriBuilderParams>
        implements Query<Participant, CaseParticipantUriBuilderParams>,
                   GetById<Participant, CaseParticipantUriBuilderParams>,
                   Create<Participant, CaseParticipantUriBuilderParams>,
                   Patch<Participant, CaseParticipantUriBuilderParams>,
                   Delete<Participant, CaseParticipantUriBuilderParams> {
  constructor(httpServiceBundle: HttpServiceBundle, protected translateService: TranslateService) {
    super(Participant, httpServiceBundle);
  }

  public query(uriParams: CaseParticipantUriBuilderParams, params?: DrQueryParams): Observable<BaseWithIdList<Participant>> {
    return this.queryBase(uriParams, params)
               .pipe(map(result => BaseWithIdList.upgradeFromBaseList(result)));
  }

  // The getById can also be used to fetch by type if the participant id part is left empty and the correct suffix is added to the DrQueryParams
  public getById(uriParams: CaseParticipantUriBuilderParams, params?: DrQueryParams): Observable<Participant> {
    return this.getBaseById(uriParams, params);
  }

  public create(uriParams: CaseParticipantUriBuilderParams, participant: Participant, params?: DrQueryParams): Observable<Participant> {
    return this.createBase(uriParams, participant, params);
  }

  public patch(uriParams: CaseParticipantUriBuilderParams, data: OpData[], params?: DrQueryParams): Observable<Participant> {
    return this.patchBase(uriParams, data, params);
  }

  public patchAndUpdate(uriParams: CaseParticipantUriBuilderParams, participant: Participant, data: OpData[], params?: DrQueryParams): Observable<Participant> {
    return this.patchAndUpdateBase(uriParams, participant, data, params);
  }

  public delete(uriParams: CaseParticipantUriBuilderParams): Observable<boolean> {
    return this.deleteBase(uriParams);
  }

  public postContacts(caseId: string, participantId: string): Observable<DRResponse<Participant>> {
    return this.http.post<DRResponse<Participant>>(`${this.uri}/cases/${caseId}/participants/${participantId}/contact`, {
      caseId: caseId,
      participantId: participantId
    });
  }

  public putContacts(caseId: string, participantId: string): Observable<DRResponse<Participant>> {
    return this.http.put<DRResponse<Participant>>(`${this.uri}/cases/${caseId}/participants/${participantId}/contact`, {
      caseId: caseId,
      participantId: participantId
    });
  }

  public createParticipant(caseId: string, participant: any): Observable<DRResponse<Participant>> {
    return this.http.post<DRResponse<Participant>>(`${this.uri}/cases/${caseId}/participants`, participant);
  }

  public getParticipantTypes(uriParams: CaseParticipantUriBuilderParams | WorkflowParticipantUriBuilderParams): Observable<BaseList<ParticipantType>> {
    const uri = `${environment.apiUrl}/${BackendUriUnits.API}${uriParams instanceof CaseParticipantUriBuilderParams ? '/' + BackendUriUnits.SMP : ''}${uriParams.buildTypeUri()}` ;

    return this.http.get<DRResponse<BaseList<ParticipantType>>>(uri)
                    .pipe(map(res => {
        return new BaseList(ParticipantType, res.data && (<any>res.data).general ? (<any>res.data).general : []);
    }));
  }

  public getTranslatedParticipantTypes(participant: { getMyTypeTranslationKeys(): TranslationEntry[] }): string {
    let translated = '';
    const translationKeys = participant.getMyTypeTranslationKeys();
    translationKeys.forEach((typeTrans: TranslationEntry) => {
      const idx = translationKeys.indexOf(typeTrans);
      translated += `${idx === 0 ? '' : ' / '}${this.translateService.instant(typeTrans.trans)}`;
    });
    return translated;
  }
}
